import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    HomeIcon,
    UserGroupIcon,
    BriefcaseIcon,
    ArrowLeftOnRectangleIcon,
    Cog6ToothIcon,
    ChevronDownIcon,
    BellIcon,
    EnvelopeIcon,
    DocumentTextIcon,
    ClipboardDocumentListIcon,
    ChartPieIcon,
    DocumentMagnifyingGlassIcon,
    SparklesIcon,
    AcademicCapIcon,
    MegaphoneIcon,
    CubeTransparentIcon,
    CameraIcon,
    ArrowUpOnSquareIcon,
    UsersIcon,
    CalendarDaysIcon,
    ClockIcon,
    PencilSquareIcon,
    TrophyIcon,
    WrenchScrewdriverIcon,
    ChatBubbleLeftRightIcon,
    Square3Stack3DIcon,
    RocketLaunchIcon,
    ScaleIcon,         // New: Compliance/Audit
    CurrencyDollarIcon, // New: Finance
    ArrowPathIcon,      // New: Turnover
    ShieldCheckIcon,    // New: Asset/Compliance
    ChartBarIcon,       // New: Recruitment Analytics
    ClipboardDocumentCheckIcon, // New: Skill Gap
    HeartIcon,          // New: Engagement
    FolderOpenIcon,     // New: Document Repository
    CommandLineIcon     // New: Talent Management
} from '@heroicons/react/24/outline';

// Mock data for notifications
const mockNotifications = [
    { id: 1, text: 'New leave request from John Doe.', unread: true },
    { id: 2, text: 'Performance review for Jane Smith is due.', unread: true },
    { id: 3, text: 'Upcoming training session on Python.', unread: false },
];

// ====================================================================
// UPDATED: Map of link names to their corresponding icons
// ====================================================================
const linkIcons = {
    'Dashboard': ChartPieIcon,
    'Employee Profiles': UserGroupIcon,
    'Team Overview': CubeTransparentIcon,
    'Recruitment Pipeline': RocketLaunchIcon, // Renamed 'Recruitment' to be consistent
    'Employee Management': UsersIcon,
    'Leave Management': CalendarDaysIcon,
    'Onboarding & Offboarding': ClipboardDocumentListIcon,
    'Attendance Reports': ClockIcon,
    'Payroll Overview': DocumentTextIcon,
    'Performance Reports': TrophyIcon,
    'Goals & Objectives': SparklesIcon,
    'Feedback Management': ChatBubbleLeftRightIcon,
    'Training Assignments': AcademicCapIcon, // Changed from WrenchScrewdriverIcon
    'Announcements': MegaphoneIcon,
    'Internal Comms': EnvelopeIcon,
    // --- New Strategic Dashboards ---
    'Finance Dashboard': CurrencyDollarIcon,
    'Recruitment Analytics': ChartBarIcon,
    'Workforce Turnover': ArrowPathIcon,
    'Compliance Audit': ScaleIcon,
    'Asset Management': ShieldCheckIcon,
    'Skill Gap Analysis': ClipboardDocumentCheckIcon,
    'Talent Management': CommandLineIcon,
    'Engagement Reports': HeartIcon,
    'Document Repository': FolderOpenIcon,
};


const dropdownMenus = [
    {
        name: 'Core HR',
        icon: BriefcaseIcon,
        links: [
            { name: 'Dashboard', path: '/employerdashboard' },
            { name: 'Employee Profiles', path: '/employerdashboard/employee-profile' },
            { name: 'Team Overview', path: '/employerdashboard/team-overview' },
            { name: 'Employee Management', path: '/employerdashboard/employee-management' },
            { name: 'Leave Management', path: '/employerdashboard/leave-management' },
        ],
    },
    {
        name: 'Acquisition',
        icon: RocketLaunchIcon,
        links: [
            { name: 'Recruitment Pipeline', path: '/employerdashboard/recruitment-pipeline' },
            // { name: 'Onboarding & Offboarding', path: '/employerdashboard/onboarding-offboarding' },
            { name: 'Recruitment Analytics', path: '/employerdashboard/recruitment-analytics' }, // NEW
        ],
    },
    {
        name: 'P&D', // Performance & Development
        icon: SparklesIcon,
        links: [
            { name: 'Performance Reports', path: '/employerdashboard/performance-reports' },
            { name: 'Goals & Objectives', path: '/employerdashboard/goals-objectives' },
            { name: 'Talent Management', path: '/employerdashboard/talent-management' }, // NEW
            { name: 'Skill Gap Analysis', path: '/employerdashboard/skill-gap-analysis' }, // NEW
            { name: 'Training Assignments', path: '/employerdashboard/training-assignments' },
            { name: 'Training Development', path: '/employerdashboard/training-development' }, // Added training-development
            { name: 'Feedback Management', path: '/employerdashboard/feedback-management' },
        ],
    },
    {
        name: 'Strategy & Analytics',
        icon: ChartPieIcon,
        links: [
            { name: 'Finance Dashboard', path: '/employerdashboard/finance-dashboard' },          // NEW
            { name: 'Workforce Turnover', path: '/employerdashboard/workforce-turnover' },        // NEW
            { name: 'Engagement Reports', path: '/employerdashboard/engagement-reports' },        // NEW
            { name: 'Compliance Audit', path: '/employerdashboard/compliance-audit' },            // NEW
        ],
    },
    {
        name: 'System',
        icon: Cog6ToothIcon,
        links: [
            { name: 'Asset Management', path: '/employerdashboard/asset-management' },            // NEW
            { name: 'Document Repository', path: '/employerdashboard/document-repository' },      // NEW
            { name: 'Payroll Overview', path: '/employerdashboard/payroll-overview' },
            { name: 'Attendance Reports', path: '/employerdashboard/attendance-reports' },
            { name: 'Announcements', path: '/employerdashboard/announcements-notifications' },
            { name: 'Internal Comms', path: '/employerdashboard/internal-communications' },
        ],
    },
];

const EmployerTopBar = () => {
    const [openDropdown, setOpenDropdown] = useState(null);
    const [notifications, setNotifications] = useState(mockNotifications);

    const handleClearNotifications = () => {
        setNotifications([]);
    };

    const handleUploadImage = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                console.log('File selected:', file.name);
                // Here you would handle the image upload logic (e.g., send to server)
                alert(`Selected file: ${file.name}`);
            }
        };
        fileInput.click();
        setOpenDropdown(null);
    };

    // ====================================================================
    // FIXED: Simplified handleTakeImage for demonstration (webcam requires a proper component/modal)
    // ====================================================================
    const handleTakeImage = async () => {
        setOpenDropdown(null);
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            alert("Camera access is not supported by your browser or connection (must be HTTPS for security).");
            return;
        }
        
        try {
            // Request camera access just to check permissions and show the OS prompt
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            
            // Immediately stop the track as we aren't displaying a video feed here
            stream.getTracks().forEach(track => track.stop()); 

            alert("Camera permission granted. In a real application, a dedicated modal with a live video feed and a 'Capture' button would appear here to take the picture.");

        } catch (error) {
            console.error("Error accessing the camera: ", error);
            alert(`Could not access camera. Please check your system settings or ensure you have granted permission. Error: ${error.message}`);
        }
    };

    const unreadCount = notifications.filter(n => n.unread).length;

    return (
        <motion.div
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="sticky top-0 z-50 bg-gradient-to-r from-blue-900 to-indigo-800 shadow-xl text-white backdrop-blur-md bg-opacity-80"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo/Brand Section */}
                    <div className="flex-shrink-0">
                        <Link to="/employerdashboard" className="flex items-center space-x-2 transition-transform hover:scale-105">
                            <span className="text-3xl font-extrabold tracking-tight text-white">HR</span>
                            <span className="text-xl font-light text-indigo-200">PRO</span>
                        </Link>
                    </div>

                    {/* Navigation Links */}
                    <nav className="hidden md:flex space-x-6 lg:space-x-8">
                        {dropdownMenus.map((menu) => (
                            <div
                                key={menu.name}
                                className="relative"
                                onMouseEnter={() => setOpenDropdown(menu.name)}
                                onMouseLeave={() => setOpenDropdown(null)}
                            >
                                <motion.button
                                    className="flex items-center text-sm font-medium transition-colors hover:text-indigo-200 p-2 rounded-md hover:bg-white/10"
                                >
                                    <menu.icon className="h-5 w-5 mr-2" />
                                    <span>{menu.name}</span>
                                    <ChevronDownIcon className={`h-4 w-4 ml-1 transform transition-transform ${openDropdown === menu.name ? 'rotate-180' : 'rotate-0'}`} />
                                </motion.button>
                                <AnimatePresence>
                                    {openDropdown === menu.name && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg py-1 text-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden"
                                        >
                                            {menu.links.map((link) => {
                                                // Get the icon from the updated linkIcons map
                                                const Icon = linkIcons[link.name] || CubeTransparentIcon; 
                                                return (
                                                    <Link
                                                        key={link.name}
                                                        to={link.path}
                                                        onClick={() => setOpenDropdown(null)} // Close dropdown on click
                                                        className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                                                    >
                                                        <Icon className="h-5 w-5 mr-2 text-gray-500" />
                                                        {link.name}
                                                    </Link>
                                                );
                                            })}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </nav>

                    {/* Right-end Profile & Icons */}
                    <div className="flex items-center space-x-4">
                        {/* Notifications Dropdown (kept as is) */}
                        <div
                            className="relative"
                            onMouseEnter={() => setOpenDropdown('notifications')}
                            onMouseLeave={() => setOpenDropdown(null)}
                        >
                            <button className="relative p-2 rounded-full text-indigo-200 hover:text-white transition-colors">
                                <BellIcon className="h-6 w-6" />
                                {unreadCount > 0 && (
                                    <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
                                )}
                            </button>
                            <AnimatePresence>
                                {openDropdown === 'notifications' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg py-2 text-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden"
                                    >
                                        <div className="px-4 py-1 text-sm text-gray-400">Notifications</div>
                                        <div className="max-h-60 overflow-y-auto">
                                            {notifications.length > 0 ? (
                                                notifications.map((notif) => (
                                                    <div key={notif.id} className={`flex items-center px-4 py-2 text-sm transition-colors ${notif.unread ? 'bg-indigo-50 font-medium' : 'hover:bg-gray-100'}`}>
                                                        <span className={`h-2 w-2 rounded-full mr-2 ${notif.unread ? 'bg-red-500' : 'bg-transparent'}`}></span>
                                                        {notif.text}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center text-gray-500 text-sm py-4">No new notifications.</div>
                                            )}
                                        </div>
                                        <div className="border-t border-gray-100 mt-2 pt-2">
                                            <button
                                                onClick={handleClearNotifications}
                                                className="w-full text-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                                            >
                                                View All
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        {/* Mail Dropdown - Placeholder (kept as is) */}
                        <div
                            className="relative"
                            onMouseEnter={() => setOpenDropdown('mail')}
                            onMouseLeave={() => setOpenDropdown(null)}
                        >
                            <button className="relative p-2 rounded-full text-indigo-200 hover:text-white transition-colors">
                                <EnvelopeIcon className="h-6 w-6" />
                                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-blue-400 ring-2 ring-white" />
                            </button>
                            <AnimatePresence>
                                {openDropdown === 'mail' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg py-2 text-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden"
                                    >
                                        <div className="text-center text-gray-500 text-sm py-4">No new messages.</div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Profile Dropdown */}
                        <div
                            className="relative"
                            onMouseEnter={() => setOpenDropdown('profile')}
                            onMouseLeave={() => setOpenDropdown(null)}
                        >
                            <button
                                className="flex items-center space-x-2 p-2 rounded-full hover:bg-white/20 transition-colors"
                            >
                                <img
                                    className="h-9 w-9 rounded-full border-2 border-indigo-400 object-cover"
                                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2940&auto=format&fit=crop"
                                    alt="User Profile"
                                />
                                <ChevronDownIcon className={`h-5 w-5 transform transition-transform ${openDropdown === 'profile' ? 'rotate-180' : 'rotate-0'}`} />
                            </button>

                            <AnimatePresence>
                                {openDropdown === 'profile' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 text-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    >
                                        <div className="px-4 py-2 text-sm text-gray-700 font-medium border-b border-gray-100">
                                            Signed in as Employer
                                        </div>
                                        <button onClick={handleUploadImage} className="flex items-center w-full px-4 py-2 text-sm hover:bg-gray-100 transition-colors">
                                            <ArrowUpOnSquareIcon className="h-5 w-5 mr-2 text-gray-500" />
                                            Upload Image
                                        </button>
                                        <button onClick={handleTakeImage} className="flex items-center w-full px-4 py-2 text-sm hover:bg-gray-100 transition-colors">
                                            <CameraIcon className="h-5 w-5 mr-2 text-gray-500" />
                                            Take Image
                                        </button>
                                        <Link to="/employerdashboard/settings" className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors">
                                            <Cog6ToothIcon className="h-5 w-5 mr-2 text-gray-500" />
                                            Settings
                                        </Link>
                                        <Link to="/login" className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors">
                                            <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2 text-gray-500" />
                                            Sign Out
                                        </Link>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default EmployerTopBar;